import styled from 'styled-components';
import { TooltipProps, TooltipPosition, ArrowAlignment } from './tooltip.types';

export const Container = styled.div<{ preserveWidth: boolean }>`
  position: relative;
  width: ${({ preserveWidth }) => (preserveWidth ? '100%' : 'max-content')};
  height: max-content;
`;

export const Tooltip = styled.div<{
  coords: { x: number; y: number };
  position: TooltipPosition;
  type: TooltipProps['type'];
  active: boolean;
  arrowAlignment: ArrowAlignment;
  arrowSize: number;
  arrowPadding: number;
  leaveOnContent: boolean;
}>`
  height: min-content;
  width: min-content;

  padding: ${({ type }) => {
    if (type === 'text') {
      return '0px 12px';
    }
    if (type === 'bigContent') {
      return '12px';
    }
    if (type === 'content') {
      return '8px';
    }
  }};
  box-sizing: border-box;

  ${({ theme: { font } }) => font.t2}
  background-color: ${({ theme: { color } }) => color.ui7};
  color: ${({ theme: { color } }) => color.ui2};
  border-radius: ${({ theme: { radius }, type }) => (type === 'bigContent' ? radius.m : radius.xs)};
  filter: ${({ theme: { shadows } }) => shadows.tooltipOuterShadow};
  box-shadow: ${({ theme: { shadows } }) => shadows.outerShadow};

  top: ${({ coords }) => `${coords.y}px`};
  left: ${({ coords }) => `${coords.x}px`};
  transform: ${({ position, arrowAlignment, arrowSize, arrowPadding }) =>
    getTransform(position, arrowAlignment, arrowSize, arrowPadding)};
  position: fixed;

  transition: 150ms opacity;
  z-index: 9999;

  opacity: ${({ active }) => (active ? 1 : 0)};
  ${({ active }) => (!active ? 'pointer-events: none;' : '')};
  white-space: ${({ type }) => (type === 'text' ? 'nowrap' : 'normal')};
`;

export const TooltipArrow = styled.svg<{
  position: TooltipPosition;
  alignment: ArrowAlignment;
  padding: number;
}>`
  ${({ position, alignment, padding }) => getTrianglePosition(position, alignment, padding)}
  transform: ${({ position }) => getTriangleTransform(position)};
  fill: ${({ theme: { color } }) => color.ui7};
  position: absolute;
  pointer-events: none; // Avoid having problems with onMouseEnter and onMouse leave hovers
`;

export const ChildrenWrapper = styled.div<{ preserveWidth: boolean }>`
  width: ${({ preserveWidth }) => (preserveWidth ? '100%' : 'max-content')};
  height: max-content;
`;

function getTransform(
  position: TooltipPosition,
  arrowAlignment: ArrowAlignment,
  arrowSize: number,
  arrowPadding: number,
) {
  switch (position) {
    case 'top': {
      if (arrowAlignment === 'start') {
        return `translate(${-arrowPadding}px, calc(-100% - ${arrowSize / 2}px))`;
      }
      if (arrowAlignment === 'center') {
        return `translate(-50%, calc(-100% - ${arrowSize / 2}px))`;
      }
      if (arrowAlignment === 'end') {
        return `translate(calc(-100% + ${arrowPadding}px), calc(-100% - ${arrowSize / 2}px))`;
      }
      break;
    }
    case 'left': {
      if (arrowAlignment === 'start') {
        return `translate(calc(-100% - ${arrowSize / 2}px),${-arrowPadding}px)`;
      }
      if (arrowAlignment === 'center') {
        return `translate(calc(-100% - ${arrowSize / 2}px), -50%)`;
      }
      if (arrowAlignment === 'end') {
        return `translate(calc(-100% - ${arrowSize / 2}px),calc(-100% - ${-arrowPadding}px))`;
      }
      break;
    }
    case 'right': {
      if (arrowAlignment === 'start') {
        return `translate(${arrowSize / 2}px, ${-arrowPadding}px)`;
      }
      if (arrowAlignment === 'center') {
        return `translate(${arrowSize / 2}px, -50%)`;
      }
      if (arrowAlignment === 'end') {
        return `translate(${arrowSize / 2}px, calc(-100% + ${arrowPadding}px))`;
      }
      break;
    }
    case 'bottom': {
      if (arrowAlignment === 'start') {
        return `translate(${-arrowPadding}px,${arrowSize / 2}px)`;
      }
      if (arrowAlignment === 'center') {
        return `translate(-50%, ${arrowSize / 2}px)`;
      }
      if (arrowAlignment === 'end') {
        return `translate(calc(-100% + ${arrowPadding}px),${arrowSize / 2}px)`;
      }
      break;
    }
  }
}

function getTrianglePosition(
  position: TooltipPosition,
  alignment: ArrowAlignment,
  arrowPadding: number,
) {
  switch (position) {
    case 'top': {
      if (alignment === 'start') {
        return { top: 'auto', left: `${arrowPadding}px`, right: '50%', bottom: '0px' };
      }
      if (alignment === 'center') {
        return { top: 'auto', left: '50%', right: '50%', bottom: '0px' };
      }
      if (alignment === 'end') {
        return { top: 'auto', left: `calc(100% - ${arrowPadding}px)`, right: '50%', bottom: '0px' };
      }
      break;
    }

    case 'left': {
      if (alignment === 'start') {
        return { top: `${arrowPadding}px`, left: 'auto', right: '0px', bottom: '50%' };
      }
      if (alignment === 'center') {
        return { top: '50%', left: 'auto', right: '0px', bottom: '50%' };
      }
      if (alignment === 'end') {
        return { top: `calc(100% - ${arrowPadding}px)`, left: 'auto', right: '0px', bottom: '50%' };
      }
      break;
    }

    case 'right': {
      if (alignment === 'start') {
        return { top: `${arrowPadding}px`, left: '0px', right: 'auto', bottom: '50%' };
      }
      if (alignment === 'center') {
        return { top: '50%', left: '0px', right: 'auto', bottom: '50%' };
      }
      if (alignment === 'end') {
        return { top: `calc(100% - ${arrowPadding}px)`, left: '0px', right: 'auto', bottom: '50%' };
      }
      break;
    }

    case 'bottom': {
      if (alignment === 'start') {
        return { top: '0px', left: `${arrowPadding}px`, right: '50%', bottom: 'auto' };
      }
      if (alignment === 'center') {
        return { top: '0px', left: '50%', right: '50%', bottom: 'auto' };
      }
      if (alignment === 'end') {
        return { top: '0px', left: `calc(100% - ${arrowPadding}px)`, right: '50%', bottom: 'auto' };
      }
      break;
    }
  }
}

function getTriangleTransform(position: TooltipPosition) {
  switch (position) {
    case 'top':
      return 'translate(-50%, 95%) rotate(180deg)';
    case 'left':
      return 'translate(95%, -50%) rotate(90deg)';
    case 'right':
      return 'translate(-95%, -50%) rotate(270deg)';
    case 'bottom':
      return 'translate(-50%, -95%) rotate(0deg)';
  }
}
